import {
    InscalarProvider as Provider,
    useInscalar,
} from '@ilg-core/inscalar-web/react'
import { useInscalarIdentity } from '@ilg-core/inscalar-web/react'
import { PropsWithChildren, useEffect, useRef, useState } from 'react'

import { InscalarSDK } from '@ilg-core/inscalar-web'
import type { InscalarConfig, LogAdapter } from '@ilg-core/inscalar-web'
import { captureException } from '@sentry/nextjs'
import { useRouter } from 'next/router'

import { SENTRY_TAG_INSCALAR } from '@constants/sentryTags'

import useAppQueryState from '@hooks/useAppQueryState'
import useInscalarTrackEvent from '@hooks/useInscalarTrackEvent'

import { getInscalarAppEnv } from '@utils/getInscalarAppEnv'
import { getCookieYesConsent, getHasAnalyticsConsent } from '@utils/inscalar'

const logAdapter: LogAdapter = {
    debug: (message: string, context) => {
        console.debug(message, context)
    },
    info: (message: string, context) => {
        console.info(message, context)
    },
    warn: (message: string, context) => {
        console.warn(message, context)
    },
    error: (message: string, context) => {
        console.error(message, context)
    },
}

const inscalarConfig: InscalarConfig = {
    projectId: process.env.NEXT_PUBLIC_INSCALAR_PROJECT_ID ?? '',
    apiKey: process.env.NEXT_PUBLIC_INSCALAR_API_KEY ?? '',
    environment: getInscalarAppEnv(),
    apiBaseUrl: process.env.NEXT_PUBLIC_INSCALAR_API_BASE_URL ?? '',
    logAdapter,
    onError: (error, context) => {
        if (process.env.NEXT_PUBLIC_APP_ENV === 'production') {
            return
        }
        captureException(error, {
            tags: { [SENTRY_TAG_INSCALAR]: true },
            extra: context,
        })
    },
    debug: process.env.NEXT_PUBLIC_APP_ENV !== 'production',
}

function InscalarConsentSync({ children }: PropsWithChildren) {
    const { isInitialized } = useInscalar()
    const [isConsentSynced, setIsConsentSynced] = useState(false)

    useEffect(() => {
        if (!isInitialized) return

        const syncConsent = async () => {
            const consent = getCookieYesConsent()

            if (!consent) {
                if (process.env.NEXT_PUBLIC_APP_ENV !== 'production') {
                    await InscalarSDK.setConsent(true, '2.1')
                } else {
                    console.warn('[Inscalar no consent]')
                }

                setIsConsentSynced(true)
                return
            }

            const hasConsent = getHasAnalyticsConsent(consent)
            await InscalarSDK.setConsent(hasConsent, '2.1')

            setIsConsentSynced(true)
        }

        syncConsent()

        window.addEventListener('cookieyes_consent_update', syncConsent)

        return () => {
            window.removeEventListener('cookieyes_consent_update', syncConsent)
        }
    }, [isInitialized])

    if (!isConsentSynced) return null

    return <>{children}</>
}

function InscalarIdentity() {
    const { appQueryState } = useAppQueryState()
    const { isInitialized } = useInscalar()
    const { identify } = useInscalarIdentity()
    const hasRun = useRef(false)

    useEffect(() => {
        if (!isInitialized || hasRun.current) return

        hasRun.current = true

        if (appQueryState.customerId) {
            identify(appQueryState.customerId, {
                userIdentifiedEventVersion: '1.2',
                profileAliasEventVersion: '2.2',
            })
        }
    }, [isInitialized, appQueryState.customerId, identify])

    return null
}

export const InscalarProvider = ({ children }: PropsWithChildren) => {
    return (
        <Provider config={inscalarConfig}>
            <InscalarConsentSync>
                <InscalarIdentity />
            </InscalarConsentSync>
            {children}
        </Provider>
    )
}
